/**
 * "Historical Validation" — a clearly separate, clearly-labeled
 * research/evaluation action for `GET /fno-analysis/{symbol}/backtest`.
 * Deliberately NOT part of the hero card (per the brief: "if you add a Run
 * Backtest affordance ... make it a clearly separate, clearly-labeled
 * research/evaluation action ... not part of the hero card") — collapsed
 * by default, only fetches once the trader explicitly picks a date range
 * and clicks "Run Historical Validation".
 */
import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScienceIcon from "@mui/icons-material/Science";
import fnoAnalysisService from "@/services/fnoAnalysisService";
import type { FnoBacktestReport } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

function fmtPct(value?: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(1)}%`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthsAgoIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

export function HistoricalValidationSection({ symbol }: { symbol: string }) {
  const [startDate, setStartDate] = useState(monthsAgoIso(6));
  const [endDate, setEndDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<FnoBacktestReport | null>(null);

  const runValidation = () => {
    setLoading(true);
    setError(null);
    fnoAnalysisService
      .getFnoBacktest(symbol, startDate, endDate)
      .then((result) => setReport(result))
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  return (
    <Accordion variant="outlined" disableGutters sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ScienceIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            Historical Validation (research only)
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Replays this signal engine over {symbol}&apos;s own past price history to evaluate how similar past
          signals would have resolved. This is a backward-looking research tool, not a live trade and not a
          guarantee of future performance.
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" onClick={runValidation} disabled={loading} fullWidth>
              {loading ? <CircularProgress size={20} /> : "Run Historical Validation"}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {report && (
          <Box>
            {report.edge_message && (
              <Alert severity={report.negative_edge ? "warning" : "info"} sx={{ mb: 2 }}>
                {report.edge_message}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Signals
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {report.total_signals}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Win Rate
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {fmtPct(report.win_rate_pct)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Profit Factor
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {report.profit_factor !== null && report.profit_factor !== undefined
                    ? report.profit_factor.toFixed(2)
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Max Drawdown
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {fmtPct(report.max_drawdown_pct)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Target 1 Hit Rate
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {fmtPct(report.target1_hit_pct)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Stop-Loss Hit Rate
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {fmtPct(report.stop_loss_hit_pct)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Avg Holding Period
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {report.avg_holding_period_days !== null && report.avg_holding_period_days !== undefined
                    ? `${report.avg_holding_period_days.toFixed(1)} days`
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              {report.resolution_note}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default HistoricalValidationSection;
